import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './ViewAllStudentsModal.module.scss';
import Modal from '../../../components/modal/Modal';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import LiveSearch from '../../../components/liveSearch/LiveSearch';
import axiosClient from '../../../api/axiosClient';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import ProgramStudentsTable from './ProgramStudentsTable';
import DropDownButton from '../../../components/dropDownButton/DropDownButton';
import AssignClinicalDaysModal from './AssignClinicalDaysModal';
import AssignNonClinicalDaysModal from './AssignNonClinicalDaysModal';
import { useToasts } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';

const ViewAllStudentsModal = ({ isOpen, onClose, programId, programTitle, onAddStudentsClick }) => {
    const { showToast } = useToasts();
    const { role, can } = useAuth();
    const canAddStudents = role !== 'Program Coordinator' || can('canAddStudents');
    const [allStudents, setAllStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [isAssignClinicalDaysOpen, setIsAssignClinicalDaysOpen] = useState(false);
    const [isAssignNonClinicalDaysOpen, setIsAssignNonClinicalDaysOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setSearchQuery('');
            setSelectedStudents([]);
        }
    }, [isOpen]);

    useEffect(() => {
        const fetchStudents = async () => {
            if (!isOpen || !programId) return;
            
            setIsLoading(true);
            try {
                const response = await axiosClient.get(`/programs/${programId}/students`);
                
                let result = response.data?.data || response.data?.students || response.data;
                const fetchedStudents = Array.isArray(result) ? result : [];

                setAllStudents(fetchedStudents);
            } catch (error) {
                showToast(error.message, 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchStudents();
    }, [isOpen, programId, showToast]);

    const handleAutoSchedule = async (studentIds, exclude) => {
        setIsLoading(true);
        try {
            const response = await axiosClient.patch(`/program-schedules/${programId}/students/auto-schedule`, {
                studentIds,
                exclude
            });
            showToast(response.message, 'success');
            setAllStudents(prev =>
                prev.map(s => studentIds.includes(s.id) ? { ...s, excludeFromAutoSchedule: exclude } : s)
            );
            setSelectedStudents([]);
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const getFilteredStudents = () => {
        if (!searchQuery) return allStudents;
        const query = searchQuery.toLowerCase();
        return allStudents.filter(s => 
            s.firstName?.toLowerCase().includes(query) || 
            s.lastName?.toLowerCase().includes(query)
        );
    };

    if (!isOpen) return null;

    return (
        <>
        <Modal scrollable={true}>
            {isLoading && <LoadingOverlay />}
            <div className={styles.modalContent}>
                <div className={styles.header}>
                    <div className={styles.titleArea}>
                        <h2 className={styles.title}>Students- {programTitle || 'Program'}</h2>
                    </div>
                    
                    <div className={styles.actions}>
                        <LiveSearch
                            placeholder="Search students"
                            onChange={(val) => setSearchQuery(val)}
                        />
                        {selectedStudents.length > 0 ? (
                            <DropDownButton
                                label="Manage Students"
                                variant="primary-light"
                                size="medium"
                                position="bottom-left"
                                items={[
                                    { label: 'Assign Clinical Days', onClick: () => setIsAssignClinicalDaysOpen(true) },
                                    { label: 'Assign Non Clinical Days', onClick: () => setIsAssignNonClinicalDaysOpen(true) },
                                    ...( allStudents.some(s => selectedStudents.includes(s.id) && s.excludeFromAutoSchedule)
                                        ? [{ label: 'Enable Auto Schedule', onClick: () => handleAutoSchedule(selectedStudents, false) }]
                                        : []
                                    ),
                                    ...( allStudents.some(s => selectedStudents.includes(s.id) && !s.excludeFromAutoSchedule)
                                        ? [{ label: 'Disable Auto Schedule', onClick: () => handleAutoSchedule(selectedStudents, true) }]
                                        : []
                                    ),
                                ]}
                            />
                        ) : canAddStudents ? (
                            <Button
                                label={
                                    <div className={styles.cta}>
                                        <Icon name="plus" />
                                        Add Students
                                    </div>
                                }
                                variant="primary-light"
                                onClick={onAddStudentsClick}
                                size='medium'
                            />
                        ) : null}
                         <button className={styles.closeBtn} onClick={onClose}>
                            <Icon name="plus" className={styles.crossIcon} />
                        </button>
                    </div>
                </div>

                <div className={styles.tableWrapper}>
                    <ProgramStudentsTable
                        students={getFilteredStudents()}
                        isSelectable={true}
                        selectedRows={selectedStudents}
                        onRowSelect={setSelectedStudents}
                        onAssignClinicalDays={(studentId) => {
                            setSelectedStudents([studentId]);
                            setIsAssignClinicalDaysOpen(true);
                        }}
                        onAssignNonClinicalDays={(studentId) => {
                            setSelectedStudents([studentId]);
                            setIsAssignNonClinicalDaysOpen(true);
                        }}
                        onAutoSchedule={handleAutoSchedule}
                    />
                </div>
            </div>
        </Modal>
        <AssignClinicalDaysModal
            isOpen={isAssignClinicalDaysOpen}
            onClose={() => setIsAssignClinicalDaysOpen(false)}
            studentIds={selectedStudents}
            onSuccess={() => setSelectedStudents([])}
        />
        <AssignNonClinicalDaysModal
            isOpen={isAssignNonClinicalDaysOpen}
            onClose={() => setIsAssignNonClinicalDaysOpen(false)}
            studentIds={selectedStudents}
            onSuccess={() => setSelectedStudents([])}
        />
        </>
    );
};

ViewAllStudentsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  programTitle: PropTypes.string,
  onAddStudentsClick: PropTypes.func
};

export default ViewAllStudentsModal;
